import { normalizeToken } from "./normalize";
import type { BeraterAnswer, BeraterCandidate } from "./types";

export type StrebenLine = "vertikal" | "horizontal";
export type EtagenbettHeight = "mittelhoch" | "hoch";
export type DesignLine = "plano" | "nova-standard" | "nova-pro";

/** Lille/Livo = vertical bars; Sona/Nova = horizontal (series name in product handle). */
export const inferStrebenLine = (handle: string): StrebenLine | null => {
  const token = normalizeToken(handle);
  if (token.includes("lille") || token.includes("livo")) return "vertikal";
  if (token.includes("sona") || token.includes("nova")) return "horizontal";
  return null;
};

/** Etagenbett height is encoded in the pool product handle (two parents). */
export const inferEtagenbettHeight = (handle: string): EtagenbettHeight | null => {
  const token = normalizeToken(handle);
  if (!token.includes("etagenbett")) return null;
  if (token.includes("mittelhoch")) return "mittelhoch";
  if (token.includes("hoch")) return "hoch";
  return null;
};

const parseStrebenToken = (token: string): { line: StrebenLine; wood: string | null } | null => {
  if (token.endsWith("vertikal")) {
    const wood = token.slice(0, -"vertikal".length);
    return { line: "vertikal", wood: wood || null };
  }
  if (token.endsWith("horizontal")) {
    const wood = token.slice(0, -"horizontal".length);
    return { line: "horizontal", wood: wood || null };
  }
  return null;
};

export const getStrebenPreference = (answers: BeraterAnswer[]): StrebenLine | null => {
  for (const answer of answers) {
    for (const value of answer.values) {
      const parsed = parseStrebenToken(normalizeToken(value));
      if (parsed) return parsed.line;
    }
  }
  return null;
};

/** Wood prefix from CMS values like "buche vertikal" — disambiguates Lille (kiefer) vs Livo (buche). */
export const getStrebenWoodHint = (answers: BeraterAnswer[]): string | null => {
  for (const answer of answers) {
    for (const value of answer.values) {
      const parsed = parseStrebenToken(normalizeToken(value));
      if (parsed?.wood) return parsed.wood;
    }
  }
  return null;
};

export const candidateMatchesStrebenPreference = (candidate: BeraterCandidate, answers: BeraterAnswer[]): boolean => {
  const streben = getStrebenPreference(answers);
  if (!streben) return true;
  if (inferStrebenLine(candidate.handle) !== streben) return false;
  const woodHint = getStrebenWoodHint(answers);
  if (woodHint && normalizeToken(candidate.attrs.wood ?? "") !== woodHint) return false;
  return true;
};

export const getHeightPreference = (answers: BeraterAnswer[]): EtagenbettHeight | null => {
  for (const answer of answers) {
    for (const value of answer.values) {
      const token = normalizeToken(value);
      if (token === "mittelhoch") return "mittelhoch";
      if (token === "hoch") return "hoch";
    }
  }
  return null;
};

const parseDesignToken = (token: string): DesignLine | null => {
  if (token === "designplano") return "plano";
  if (token === "designnovastandard") return "nova-standard";
  if (token === "designnovapro") return "nova-pro";
  return null;
};

/** CMS `step-serie-jugend-buche` values: design-plano / design-nova-standard / design-nova-pro. */
export const getDesignPreference = (answers: BeraterAnswer[]): DesignLine | null => {
  for (const answer of answers) {
    for (const value of answer.values) {
      const parsed = parseDesignToken(normalizeToken(value));
      if (parsed) return parsed;
    }
  }
  return null;
};

/** Parent `jugendbett-nova` has neither standard nor pro — not a design match. */
export const inferDesignLine = (handle: string): DesignLine | null => {
  const token = normalizeToken(handle);
  if (token.includes("nova") && token.includes("standard")) return "nova-standard";
  if (token.includes("nova") && token.includes("pro")) return "nova-pro";
  if (token.includes("plano") && !token.includes("nova")) return "plano";
  return null;
};

const candidateMatchesStreben = (candidate: BeraterCandidate, want: StrebenLine): boolean =>
  inferStrebenLine(candidate.handle) === want;

const candidateMatchesHeight = (candidate: BeraterCandidate, want: EtagenbettHeight): boolean =>
  inferEtagenbettHeight(candidate.handle) === want;

const candidateHasStrebenLine = (candidate: BeraterCandidate): boolean => inferStrebenLine(candidate.handle) !== null;

const candidateHasHeight = (candidate: BeraterCandidate): boolean => inferEtagenbettHeight(candidate.handle) !== null;

/** Prefer Lille vs Sona/Livo/Nova, or Etagenbett hoch vs mittelhoch, from `attribute: none` steps. */
export const preferLineMatches = (pool: BeraterCandidate[], answers: BeraterAnswer[]): BeraterCandidate[] => {
  const design = getDesignPreference(answers);
  if (design) {
    return pool.filter((candidate) => inferDesignLine(candidate.handle) === design);
  }

  const streben = getStrebenPreference(answers);
  if (streben) {
    const matching = pool.filter((candidate) => candidateMatchesStrebenPreference(candidate, answers));
    if (matching.length) return matching;
    const lineOnly = pool.filter((candidate) => candidateMatchesStreben(candidate, streben));
    if (lineOnly.length) return lineOnly;
    const wildcard = pool.filter((candidate) => !candidateHasStrebenLine(candidate));
    return wildcard.length ? wildcard : pool;
  }

  const height = getHeightPreference(answers);
  if (height) {
    const matching = pool.filter((candidate) => candidateMatchesHeight(candidate, height));
    if (matching.length) return matching;
    const wildcard = pool.filter((candidate) => !candidateHasHeight(candidate));
    return wildcard.length ? wildcard : pool;
  }

  return pool;
};

/** 2 = exact match, 1 = no line trait (wildcard), 0 = wrong line/height. */
export const lineMatchRank = (candidate: BeraterCandidate, answers: BeraterAnswer[]): number => {
  const design = getDesignPreference(answers);
  if (design) return inferDesignLine(candidate.handle) === design ? 2 : 0;

  const streben = getStrebenPreference(answers);
  if (streben) {
    if (candidateMatchesStrebenPreference(candidate, answers)) return 2;
    const line = inferStrebenLine(candidate.handle);
    if (!line) return 1;
    return 0;
  }

  const height = getHeightPreference(answers);
  if (height) {
    const productHeight = inferEtagenbettHeight(candidate.handle);
    if (!productHeight) return 1;
    return productHeight === height ? 2 : 0;
  }

  return 0;
};

const designSelfCheckAnswers = (value: string): BeraterAnswer[] => [
  { stepHandle: "step-serie-jugend-buche", attribute: "none", values: [value] },
];

// ponytail: module self-check — Jugend Buche serie tokens must beat cheapest-Nova sort
if (inferDesignLine("jugendbett-plano-buche") !== "plano") {
  throw new Error("[ek-berater] candidateLine self-check failed (plano handle)");
}
if (inferDesignLine("jugendbett-nova-pro-natur") !== "nova-pro") {
  throw new Error("[ek-berater] candidateLine self-check failed (nova-pro handle)");
}
if (inferDesignLine("jugendbett-nova-standard-natur") !== "nova-standard") {
  throw new Error("[ek-berater] candidateLine self-check failed (nova-standard handle)");
}
if (inferDesignLine("jugendbett-nova") !== null) {
  throw new Error("[ek-berater] candidateLine self-check failed (nova parent is not a design line)");
}
if (getDesignPreference(designSelfCheckAnswers("design-plano")) !== "plano") {
  throw new Error("[ek-berater] candidateLine self-check failed (design-plano token)");
}
if (getDesignPreference(designSelfCheckAnswers("design-nova-pro")) !== "nova-pro") {
  throw new Error("[ek-berater] candidateLine self-check failed (design-nova-pro token)");
}

const designSelfCheckPool: BeraterCandidate[] = [
  { handle: "jugendbett-nova", title: "Jugendbett Nova", url: "", attrs: { age: null, bed_type: null, wood: null, material: null, sizeRaw: null }, available: true, priceMin: 19990 },
  { handle: "jugendbett-nova-standard-natur", title: "Jugendbett Nova Standard", url: "", attrs: { age: null, bed_type: null, wood: null, material: null, sizeRaw: null }, available: true, priceMin: 19990 },
  { handle: "jugendbett-nova-pro-natur", title: "Jugendbett Nova Pro", url: "", attrs: { age: null, bed_type: null, wood: null, material: null, sizeRaw: null }, available: true, priceMin: 26990 },
  { handle: "jugendbett-plano-buche", title: "Jugendbett Plano", url: "", attrs: { age: null, bed_type: null, wood: null, material: null, sizeRaw: null }, available: true, priceMin: 34990 },
];
const planoPicks = preferLineMatches(designSelfCheckPool, designSelfCheckAnswers("design-plano")).map((c) => c.handle);
const proPicks = preferLineMatches(designSelfCheckPool, designSelfCheckAnswers("design-nova-pro")).map((c) => c.handle);
if (planoPicks.length !== 1 || planoPicks[0] !== "jugendbett-plano-buche") {
  throw new Error("[ek-berater] candidateLine self-check failed (plano pool)");
}
if (proPicks.length !== 1 || proPicks[0] !== "jugendbett-nova-pro-natur") {
  throw new Error("[ek-berater] candidateLine self-check failed (nova-pro pool)");
}
